/**
 * useServerFilterTemplate - Server-side variant of useFilterTemplate.
 *
 * Same filter state + handlers as useFilterTemplate (so it plugs straight into
 * FilterIcon / FilterTemplateDropdown), but instead of filtering an in-memory
 * array it serialises the state into an `af` JSON query param that the backend
 * translates to SQL. Use for server-paginated grids (e.g. Part Rate DB, ~370k
 * rows) where the full row set is never loaded in the browser.
 *
 * @param {Object} options
 * @param {Array<{key: string, label: string}>} options.columns - Column definitions
 * @param {Object<string, Array<string>>} options.uniqueValues - col -> distinct values
 *   (from the backend; include '(Blank)' as the last entry when blanks exist).
 *   Only needed for columns that show the "Select Values" tab.
 * @param {Array<string>} [options.numericColumnKeys] - Columns using numeric operators
 * @returns Filter state, handlers, and `afParam` (JSON string or null) for the API
 */
import { useState, useCallback, useMemo } from 'react';

export default function useServerFilterTemplate({ columns = [], uniqueValues = {}, numericColumnKeys = [] }) {
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterMode, setFilterMode] = useState({});
  const [filterText, setFilterText] = useState({});
  const [filterButtonPositions, setFilterButtonPositions] = useState({});

  const getUniqueValues = useCallback(
    (columnKey) => uniqueValues[columnKey] || [],
    [uniqueValues]
  );

  const handleFilterToggle = useCallback((columnKey, value) => {
    setColumnFilters(prev => {
      const allValues = uniqueValues[columnKey] || [];
      const current = prev[columnKey];

      if (!current) {
        // All selected -> unchecking one keeps everything else.
        const newValues = allValues.filter(v => v !== value);
        return { ...prev, [columnKey]: newValues };
      }
      if (current.includes(value)) {
        const newValues = current.filter(v => v !== value);
        if (newValues.length === 0) {
          const { [columnKey]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [columnKey]: newValues };
      }
      const newValues = [...current, value];
      if (newValues.length >= allValues.length) {
        // Everything checked again -> no filter.
        const { [columnKey]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnKey]: newValues };
    });
  }, [uniqueValues]);

  // Explicit [] = uncheck all in the dropdown. Deleting the key means
  // "no filter / all selected" and FilterTemplateDropdown draws every box checked.
  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters((prev) => ({ ...prev, [columnKey]: [] }));
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const selectAllColumnFilter = useCallback((columnKey) => {
    setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
  }, []);

  const applyTextFilter = useCallback((columnKey, mode, text) => {
    const isEmptyMode = ['empty', 'notEmpty'].includes(mode);
    if (!text && !isEmptyMode) {
      setFilterMode(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
      setFilterText(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    } else {
      setFilterMode(prev => ({ ...prev, [columnKey]: mode }));
      setFilterText(prev => ({ ...prev, [columnKey]: isEmptyMode ? '' : text }));
    }
    setColumnFilters(prev => { const { [columnKey]: _, ...rest } = prev; return rest; });
    setOpenFilterColumn(null);
    setFilterSearch('');
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setFilterMode({});
    setFilterText({});
    setFilterSearch('');
    setOpenFilterColumn(null);
  }, []);

  const getActiveFilters = useCallback(() => {
    const active = [];
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) active.push({ key, type: 'values', count: values.length });
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      if (mode && filterText[key] !== undefined) active.push({ key, type: 'text', mode, value: filterText[key] });
    });
    return active;
  }, [columnFilters, filterMode, filterText]);

  const openFilterAtPosition = useCallback((e, columnKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterButtonPositions(prev => ({
      ...prev,
      [columnKey]: { top: rect.bottom + 5, left: rect.left },
    }));
    setFilterSearch('');
    setOpenFilterColumn(prev => (prev === columnKey ? null : columnKey));
  }, []);

  // Serialised filter state for the backend (`af` query param). Null when no
  // filters are active so callers can skip the param entirely.
  const afParam = useMemo(() => {
    const af = {};
    Object.entries(columnFilters).forEach(([key, values]) => {
      if (values && values.length > 0) af[key] = { ...(af[key] || {}), values };
    });
    Object.entries(filterMode).forEach(([key, mode]) => {
      if (mode) af[key] = { ...(af[key] || {}), mode, text: filterText[key] ?? '' };
    });
    return Object.keys(af).length ? JSON.stringify(af) : null;
  }, [columnFilters, filterMode, filterText]);

  const columnLabels = useMemo(() => {
    const map = {};
    columns.forEach(c => { map[c.key] = c.label || c.key; });
    return map;
  }, [columns]);

  return {
    afParam,
    columnFilters,
    openFilterColumn,
    filterSearch,
    filterMode,
    filterText,
    filterButtonPositions,
    setOpenFilterColumn,
    setFilterSearch,
    setFilterButtonPositions,
    getUniqueValues,
    handleFilterToggle,
    clearColumnFilter,
    selectAllColumnFilter,
    applyTextFilter,
    clearAllFilters,
    getActiveFilters,
    openFilterAtPosition,
    numericColumnKeys,
    columnLabels,
  };
}
